import { toast } from "sonner";
import {
  RotateCcw,
  Share2,
  Bot,
  Hand,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn, formatEtherWithDecimals, formatNumberTruncate } from "@/lib/utils";
import { formatUSDT } from "@/lib/mockData";
import { useTranslation } from "react-i18next";
import { getTokenName, INTEGRAL_COIN_CONTRACT } from "@/config/common";
import { USDT_ADDRESS, CCC_AIF_ADDRESS } from "@/config/env";

export interface HistoryOrderView {
  id: string;
  coin: string;
  type: string;
  orderSource: "auto" | "manual";
  strategy?: string;
  margin: number;
  leverage: number;
  pnl: number;
  truthPnl: number;
  pnlDisplay: number;
  entryPrice: number;
  closePrice?: number;
  openTime: string;
  closeTime: string;
  /** 盈利时：用户静态分成 USDT */
  closeSplitStaticUsdt: number;
  /** 亏损时：用户补偿 TSKx */
  closeSplitLossUserTskx: number;
  /** 盈利时：到账组成里的 USDT（63%） */
  closeSplitStaticOnlyUsdt: number;
  /** 盈利时：到账组成里的 TSK 对应 U 价值（7%） */
  closeSplitStaticTskU: number;
  /** 订单静态收益比例（千分比） */
  orderStaticRatio?: number;
  /** 订单静态纯 U 比例（千分比） */
  orderStaticOnlyURatio?: number;
  /** 订单静态 U 转 TSK 比例（千分比） */
  orderStaticU2TskRatio?: number;
  /** 订单亏损后获得 TSKx 比例（千分比） */
  orderCloseLossTskxRatio?: number;
  /** 交易手续费（USDT） */
  tradeFeeTotalUsdtAmount: number;
  /** 算力费用（USDT） */
  signalFeeTotalUsdtAmount: number;
}

const C = {
  card2: "rgba(22,26,34,0.72)",
  border: "rgba(255,255,255,0.07)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  dim: "#64748b",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
  blue: "#3b82f6",
  cyan: "#06b6d4",
};

const DIALOG_H5 = "max-w-sm w-full mx-auto sm:max-w-sm";

const COIN_LOGOS: Record<string, string> = {
  BTC: "https://cryptologos.cc/logos/bitcoin-btc-logo.svg",
  ETH: "https://cryptologos.cc/logos/ethereum-eth-logo.svg",
  BNB: "https://cryptologos.cc/logos/bnb-bnb-logo.svg",
  SOL: "https://cryptologos.cc/logos/solana-sol-logo.svg",
};

function CoinLogo({ coin, size = 18 }: { coin: string; size?: number }) {
  const src = COIN_LOGOS[coin];
  if (!src) {
    return (
      <span className="font-bold" style={{ fontSize: size * 0.7 }}>
        {coin[0]}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={coin}
      style={{ width: size, height: size }}
      className="rounded-full"
    />
  );
}

function DirBadge({ dir }: { dir: string }) {
  const { t } = useTranslation();
  const isLong = dir === "多";
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold mono-num"
      style={{
        background: isLong ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
        color: isLong ? C.green : C.red,
        border: `1px solid ${isLong ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
      }}
    >
      {isLong ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {isLong ? t("aiTradingPage.badgeLong") : t("aiTradingPage.badgeShort")}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation();
  const isAuto = source === "auto";
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
      style={{
        background: isAuto ? "rgba(34,197,94,0.10)" : "rgba(59,130,246,0.10)",
        color: isAuto ? C.green : C.blue,
        border: `1px solid ${isAuto ? "rgba(34,197,94,0.20)" : "rgba(59,130,246,0.20)"}`,
      }}
    >
      {isAuto ? <Bot size={9} /> : <Hand size={9} />}
      {isAuto ? t("aiTradingPage.badgeAuto") : t("aiTradingPage.badgeManual")}
    </span>
  );
}

export default function HistoryDetailDialog({
  order,
  onClose,
  onReorder,
}: {
  order: HistoryOrderView | null;
  onClose: () => void;
  onReorder: (coin: string) => void;
}) {
  const { t } = useTranslation();
  const tokenNameUsdt = getTokenName(USDT_ADDRESS);
  const tokenNameTsk = getTokenName(CCC_AIF_ADDRESS);
  const tokenNameTskx = getTokenName(INTEGRAL_COIN_CONTRACT);
  if (!order) return null;
  const isProfit = order.pnlDisplay >= 0;
  const contractPnl = Number(order.pnl ?? 0);
  const tradeFeeUsdt = Number(order.tradeFeeTotalUsdtAmount ?? 0);
  const signalFeeUsdt = Number(order.signalFeeTotalUsdtAmount ?? 0);
  const totalFeesUsdt = tradeFeeUsdt + signalFeeUsdt;
  const toPercentText = (permille?: number) => {
    const pct = Number(permille ?? 0) / 10;
    return Number.isInteger(pct) ? `${pct}` : pct.toFixed(1);
  };
  const staticTotalPct = toPercentText(order.orderStaticRatio);
  const staticOnlyUPct = toPercentText(order.orderStaticOnlyURatio);
  const staticTskPct = toPercentText(order.orderStaticU2TskRatio);
  const lossTskxPct = toPercentText(order.orderCloseLossTskxRatio);

  // const feeUsdStr = `$${formatEtherWithDecimals(Math.abs(totalFeesUsdt))}`;
  // const contractPnlUsdStr = `$${formatUSDT(Math.abs(contractPnl))}`;

  const metaRows: [string, string][] = [
    [t("aiTradingPage.ordersMargin"), `$${formatUSDT(order.margin)}`],
    [
      t("aiTradingPage.historyFieldEntryPrice"),
      `$${formatNumberTruncate(order.entryPrice)}`,
    ],
    [
      t("aiTradingPage.historyFieldClosePrice"),
      `$${formatNumberTruncate(order.closePrice ?? 0)}`,
    ],
    [t("aiTradingPage.detailFieldOpenTime"), order.openTime],
    [t("aiTradingPage.historyFieldCloseTime"), order.closeTime || "--"],
  ];

  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent
        className={cn(DIALOG_H5, "max-h-[85vh] overflow-y-auto")}
        style={{
          background: "#0d0f14",
          border: `1px solid ${isProfit ? "rgba(34,197,94,0.20)" : "rgba(239,68,68,0.20)"}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CoinLogo coin={order.coin} size={20} />
            <span>{order.coin}/USDT</span>
            <DirBadge dir={order.type} />
            <span className="text-xs mono-num" style={{ color: C.dim }}>
              {order.leverage}x
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <div className="text-center py-1">
            <div
              className="text-xl font-bold mono-num"
              style={{ color: isProfit ? C.green : C.red }}
            >
              {isProfit ? "+" : ""}$
              {isProfit
                ? formatUSDT(order.truthPnl)
                : formatUSDT(order.truthPnl)}
            </div>
            <div className="text-[10px] mt-0.5" style={{ color: C.dim }}>
              {order.orderSource === "auto"
                ? t("aiTradingPage.historyPnlSubtitleAuto")
                : t("aiTradingPage.historyPnlSubtitleManual")}{" "}
              ·{" "}
              {(order as { strategy?: string }).strategy ||
                t("aiTradingPage.historyStrategyDefault")}
            </div>
          </div>

          <div
            className="rounded-xl p-3.5 space-y-2 text-xs"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            {metaRows.map(([k, v]) => (
              <div key={k as string} className="flex justify-between">
                <span style={{ color: C.muted }}>{k}</span>
                <span className="mono-num">{v}</span>
              </div>
            ))}
          </div>

          <div
            className="rounded-xl overflow-hidden"
            style={{
              border: `1px solid ${isProfit ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}`,
            }}
          >
            <div
              className="px-4 py-2.5 flex items-center gap-2"
              style={{
                background: isProfit
                  ? "rgba(34,197,94,0.08)"
                  : "rgba(239,68,68,0.08)",
              }}
            >
              <span
                className="text-xs font-semibold"
                style={{ color: isProfit ? C.green : C.red }}
              >
                {isProfit
                  ? t("aiTradingPage.historyProfitBreakdownTitle")
                  : t("aiTradingPage.historyLossBreakdownTitle")}
              </span>
            </div>
            <div
              className="px-4 py-3 space-y-0 text-xs"
              style={{ background: C.card2 }}
            >
              {isProfit ? (
                <>
                  <div className="flex justify-between items-center py-2.5">
                    <span className="font-medium" style={{ color: C.text }}>
                      {t("aiTradingPage.historyOriginalProfit")}
                    </span>
                    <span
                      className="mono-num text-sm font-bold"
                      style={{ color: C.green }}
                    >
                      {order.pnl >= 0 ? "+" : "-"}$
                      {formatUSDT(Math.abs(order.pnl ?? 0))}
                    </span>
                  </div>

                  <div
                    className="rounded-lg px-3 py-2.5 my-1"
                    style={{
                      background: "rgba(239,68,68,0.04)",
                      border: "1px solid rgba(239,68,68,0.08)",
                    }}
                  >
                    <div
                      className="text-[10px] font-semibold mb-2 uppercase tracking-wide"
                      style={{ color: C.dim }}
                    >
                      {t("aiTradingPage.historyFeeSection")}
                    </div>
                    <div className="flex justify-between py-1">
                      <span style={{ color: C.muted }}>
                        {t(
                          "aiTradingPage.strategyWizard.feeCompositionAndRange"
                        )}
                      </span>
                    </div>
                    {/* <div className="flex justify-between py-1">
                      <span style={{ color: C.muted }}>
                        {t("aiTradingPage.historyFeeTrade012")}
                      </span>
                      <span className="mono-num" style={{ color: C.red }}>
                        -$
                        {formatEtherWithDecimals(Math.abs(tradeFeeUsdt))}
                      </span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span style={{ color: C.muted }}>
                        {t("aiTradingPage.historyFeeCompute015")}
                      </span>
                      <span className="mono-num" style={{ color: C.red }}>
                        -$
                        {formatEtherWithDecimals(Math.abs(signalFeeUsdt))}
                      </span>
                    </div> */}
                    <div
                      className="h-px my-1.5"
                      style={{ background: "rgba(239,68,68,0.12)" }}
                    />
                    <div className="flex justify-between py-0.5">
                      <span
                        className="font-semibold"
                        style={{ color: C.muted }}
                      >
                        {t("aiTradingPage.historyFeeSum")}
                      </span>
                      <span
                        className="mono-num font-bold"
                        style={{ color: C.red }}
                      >
                        -$
                        {formatEtherWithDecimals(Math.abs(totalFeesUsdt))}
                      </span>
                    </div>
                  </div>
                  <div className="h-px my-1" style={{ background: C.border }} />

                  <div className="flex justify-between items-center py-2">
                    <span className="font-medium" style={{ color: C.text }}>
                      {t("aiTradingPage.historyNetProfitLabel")}
                    </span>
                    <span
                      className="mono-num text-sm font-bold"
                      style={{ color: C.green }}
                    >
                      {order.pnlDisplay >= 0 ? "+" : ""}$
                      {formatUSDT(order.pnlDisplay)}
                    </span>
                  </div>

                  <div
                    className="flex justify-between items-center py-2.5 rounded-lg px-3 -mx-1"
                    style={{
                      background: "rgba(34,197,94,0.08)",
                      border: "1px solid rgba(34,197,94,0.15)",
                    }}
                  >
                    <div>
                      <span className="font-bold" style={{ color: C.green }}>
                        {t("aiTradingPage.historyUserAllocation", {
                          pct: staticTotalPct,
                        })}
                      </span>
                      <div
                        className="text-[9px] mt-0.5"
                        style={{ color: C.dim }}
                      >
                        {t("aiTradingPage.historyUserAllocationReceivedHint")}
                      </div>
                    </div>
                    <span
                      className="mono-num text-base font-extrabold"
                      style={{ color: C.green }}
                    >
                      +${formatUSDT(order.closeSplitStaticUsdt)}
                    </span>
                  </div>

                  <div
                    className="h-px mt-3 mb-1"
                    style={{ background: C.border }}
                  />
                  <div
                    className="text-[10px] font-semibold mb-1.5 uppercase tracking-wide"
                    style={{ color: C.dim }}
                  >
                    {t("aiTradingPage.historyArrivalMix")}
                  </div>
                  <div className="flex justify-between items-center py-1.5 pl-3">
                    <span style={{ color: C.muted }}>
                      {t("aiTradingPage.historyLineUsdtPct", {
                        tokenNameUsdt,
                        pct: staticOnlyUPct,
                      })}
                    </span>
                    <span
                      className="mono-num font-semibold"
                      style={{ color: C.green }}
                    >
                      +${formatUSDT(order.closeSplitStaticOnlyUsdt)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 pl-3">
                    <span style={{ color: C.muted }}>
                      {t("aiTradingPage.historyLineTskPct", {
                        tokenNameTsk,
                        pct: staticTskPct,
                      })}
                    </span>
                    <span
                      className="mono-num font-semibold"
                      style={{ color: C.green }}
                    >
                      +${formatUSDT(order.closeSplitStaticTskU)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center py-2.5">
                    <span className="font-medium" style={{ color: C.text }}>
                      {contractPnl > 0
                        ? t("aiTradingPage.historyContractProfitLabel")
                        : t("aiTradingPage.historyContractLossLabel")}
                    </span>
                    <span
                      className="mono-num text-sm font-bold"
                      style={{ color: contractPnl > 0 ? C.green : C.red }}
                    >
                      {contractPnl > 0 ? "+" : "-"}$
                      {formatUSDT(Math.abs(order.pnl))}
                    </span>
                  </div>

                  <div
                    className="rounded-lg px-3 py-2.5 my-1"
                    style={{
                      background: "rgba(239,68,68,0.04)",
                      border: "1px solid rgba(239,68,68,0.08)",
                    }}
                  >
                    <div
                      className="text-[10px] font-semibold mb-2 uppercase tracking-wide"
                      style={{ color: C.dim }}
                    >
                      {t("aiTradingPage.historyFeeSection")}
                    </div>
                    <div className="flex justify-between py-1">
                      <span style={{ color: C.muted }}>
                        {t(
                          "aiTradingPage.strategyWizard.feeCompositionAndRange"
                        )}
                      </span>
                    </div>
                    {/* <div className="flex justify-between py-1">
                      <span style={{ color: C.muted }}>
                        {t("aiTradingPage.historyFeeTrade012")}
                      </span>
                      <span className="mono-num" style={{ color: C.red }}>
                        -$
                        {formatEtherWithDecimals(Math.abs(tradeFeeUsdt))}
                      </span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span style={{ color: C.muted }}>
                        {t("aiTradingPage.historyFeeCompute015")}
                      </span>
                      <span className="mono-num" style={{ color: C.red }}>
                        -$
                        {formatEtherWithDecimals(Math.abs(signalFeeUsdt))}
                      </span>
                    </div> */}
                    <div
                      className="h-px my-1.5"
                      style={{ background: "rgba(239,68,68,0.12)" }}
                    />
                    <div className="flex justify-between py-0.5">
                      <span
                        className="font-semibold"
                        style={{ color: C.muted }}
                      >
                        {t("aiTradingPage.historyFeeSum")}
                      </span>
                      <span
                        className="mono-num font-bold"
                        style={{ color: C.red }}
                      >
                        -$
                        {formatEtherWithDecimals(Math.abs(totalFeesUsdt))}
                      </span>
                    </div>
                  </div>

                  <div className="h-px my-1" style={{ background: C.border }} />
                  <div
                    className="flex justify-between items-center py-2 rounded-lg px-3 -mx-1"
                    style={{
                      background: "rgba(239,68,68,0.06)",
                      border: "1px solid rgba(239,68,68,0.12)",
                    }}
                  >
                    <span className="font-bold" style={{ color: C.red }}>
                      {t("aiTradingPage.historyNetLossLabel")}
                    </span>
                    <span
                      className="mono-num text-sm font-extrabold"
                      style={{ color: C.red }}
                    >
                      {order.pnlDisplay >= 0 ? "+" : ""}$
                      {formatUSDT(order.pnlDisplay)}
                    </span>
                  </div>

                  <div
                    className="h-px mt-3 mb-1"
                    style={{ background: C.border }}
                  />
                  <div
                    className="text-[10px] font-semibold mb-1.5 uppercase tracking-wide"
                    style={{ color: C.dim }}
                  >
                    {t("aiTradingPage.historyTskxPointsTitle", {
                      tokenNameTskx,
                    })}
                  </div>
                  <div
                    className="flex justify-between items-center py-2 pl-3 rounded-lg"
                    style={{ background: "rgba(245,158,11,0.05)" }}
                  >
                    <span style={{ color: C.amber }}>
                      {t("aiTradingPage.historyLineTskxComp", {
                        tokenNameTskx,
                        pct: lossTskxPct,
                      })}
                    </span>
                    <span
                      className="mono-num font-bold"
                      style={{ color: C.amber }}
                    >
                      +{formatUSDT(order.closeSplitLossUserTskx)}{" "}
                      {tokenNameTskx}
                    </span>
                  </div>
                  {/* <div
                    className="text-[9px] font-medium pl-3 pt-1"
                    style={{ color: C.dim }}
                  >
                    {contractPnl > 0
                      ? t("aiTradingPage.historyCompFormulaProfit", {
                          fee: feeUsdStr,
                          pnl: contractPnlUsdStr,
                        })
                      : t("aiTradingPage.historyCompFormulaLoss", {
                          loss: contractPnlUsdStr,
                          fee: feeUsdStr,
                        })}
                  </div> */}
                </>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              className="flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5"
              style={{
                background: "rgba(6,182,212,0.10)",
                color: C.cyan,
                border: "1px solid rgba(6,182,212,0.25)",
              }}
              onClick={() => onReorder(order.coin)}
            >
              <RotateCcw size={11} />
              {t("aiTradingPage.historyReorderBtn")}
            </button>
            <button
              className="flex-1 py-2 rounded-lg text-xs font-bold"
              style={{
                background: "rgba(255,255,255,0.04)",
                color: C.dim,
                border: `1px solid ${C.border}`,
              }}
              onClick={() => toast.info(t("aiTradingPage.toastShareSoon"))}
            >
              <Share2 size={11} className="inline mr-1" />
              {t("aiTradingPage.historyShareBtn")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
